/**
 * Factura Calculations Utilities
 * Pure functions for invoice calculations
 */

import type { OrdenDisponible, FacturaDetalle } from "@/hooks/use-facturacion";

/**
 * Calcula el total sumando todas las órdenes
 * @param ordenes - Array de órdenes disponibles
 * @returns Monto total de todas las órdenes
 */
export function calcularTotalOrdenes(ordenes: OrdenDisponible[]): number {
  return ordenes.reduce((sum, orden) => sum + parseFloat(orden.total), 0);
}

/**
 * Calcula el descuento total por pagos directos
 * Suma el total de todas las órdenes que tienen tipo_cobro === "DIRECTO"
 * @param ordenes - Array de órdenes disponibles
 * @returns Monto total del descuento por pagos directos
 */
export function calcularDescuentoPagosDirectos(ordenes: OrdenDisponible[]): number {
  return ordenes
    .filter((orden) => orden.tipo_cobro === "DIRECTO")
    .reduce((sum, orden) => sum + parseFloat(orden.total), 0);
}

/**
 * Calcula el vuelto en tiempo real para un pago
 * @param saldo - Saldo pendiente de la factura
 * @param montoRecibido - Monto recibido del cliente
 * @returns Objeto con montoAplicado, vuelto y restante
 */
export function calcularVuelto(
  saldo: number,
  montoRecibido: number
): { montoAplicado: number; vuelto: number; restante: number } {
  // Tratar valores negativos como 0
  const saldoPositivo = Math.max(0, saldo);
  const montoPositivo = Math.max(0, montoRecibido);

  if (montoPositivo >= saldoPositivo) {
    // Pago completo o con exceso
    return {
      montoAplicado: saldoPositivo,
      vuelto: montoPositivo - saldoPositivo,
      restante: 0,
    };
  } else {
    // Pago parcial
    return {
      montoAplicado: montoPositivo,
      vuelto: 0,
      restante: saldoPositivo - montoPositivo,
    };
  }
}

/**
 * Agrupa detalles de factura por orden_numero
 * @param detalles - Array de detalles de factura
 * @returns Objeto con detalles agrupados por orden_numero (clave vacía para detalles sin orden)
 */
export function agruparDetallesPorOrden(
  detalles: FacturaDetalle[]
): Record<string, FacturaDetalle[]> {
  const grupos = Object.create(null) as Record<string, FacturaDetalle[]>;
  for (const detalle of detalles) {
    const key = detalle.orden_numero ?? "";
    if (!Object.prototype.hasOwnProperty.call(grupos, key)) {
      grupos[key] = [];
    }
    grupos[key].push(detalle);
  }
  return grupos;
}
